import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useSofiaEventListener } from "@/hooks/useSofiaEventListener"
import { getShortName } from "@/lib/get-short-name"
import { cn } from "@/lib/utils"
import { ChatThreadItem } from "@/modules/chats/components/chat-thread/chat-thread-item"
import { getChatThreadByChatAppointment } from "@/modules/chats/lib/get-chat-thread-by-chat-appointment"
import { getChatRoute } from "@/modules/chats/lib/routing-helpers"
import { useChatAppointmentsQuery$data } from "@/modules/chats/services/__generated__/useChatAppointmentsQuery.graphql"
import { useMarkAllThreadMessagesAsReadMutation } from "@/modules/chats/services/useMarkAllThreadMessagesAsReadMutation"
import { ChatThread } from "@/modules/chats/types/chat-thread"
import { GenderIcon } from "@/modules/patients/components/gender-icon"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"

export type ChatAppointmentEdge = NonNullable<
  NonNullable<useChatAppointmentsQuery$data["chatsTakenByMe"]>["edges"]
>[number]

const UnreadMobileIndicator = (props: { unreadCount: number }) => {
  return props.unreadCount ? <div className="bg-critical absolute top-0.5 left-4.25 h-1.5 w-1.5 rounded-full" /> : null
}

export const ChatThreadList = (props: {
  chatAppointmentEdges: ReadonlyArray<ChatAppointmentEdge>
  selectedThread?: ChatThread
}) => {
  const { chatAppointmentEdges, selectedThread } = props

  const isDirty = useDrawerStore((state) => state.isDirty)
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)

  const confirm = useConfirmStore((state) => state.confirm)
  const navigate = useNavigate()

  const [isNavigationDisabled, setIsNavigationDisabled] = useState(false)
  const [markAllThreadMessagesAsRead] = useMarkAllThreadMessagesAsReadMutation()

  useSofiaEventListener("AI_FEATURE_RUNNING_EVENT", (e) => {
    setIsNavigationDisabled(e.detail)
  })

  const selectedThreadId = selectedThread?.id

  useEffect(() => {
    if (!selectedThreadId) {
      return
    }

    markAllThreadMessagesAsRead({
      variables: {
        input: { threadId: selectedThreadId },
      },
    })
  }, [selectedThreadId, markAllThreadMessagesAsRead])

  const handleClick = async (chatThread: ChatThread) => {
    if (isNavigationDisabled) {
      return
    }

    FaroInsightsWrapper.pushEvent("chat_select_patient", {
      chatThreadId: chatThread.id,
      patientId: chatThread.patient.id,
      appointmentId: chatThread.appointmentId,
    })

    if (isDirty) {
      const confirmed = await confirm({})
      if (!confirmed) return
    }

    if (chatThread.id === selectedThread?.id) {
      return
    }

    closeDrawer()
    const route = getChatRoute(chatThread.id)
    navigate(route, { replace: true })
  }

  const chatAppointmentEdgesNewestFirst = useMemo(
    () =>
      [...chatAppointmentEdges].sort((a, b) => {
        const aCreatedOn = a.node.chat?.lastMessage.createdOn
        const bCreatedOn = b.node.chat?.lastMessage.createdOn
        if (!aCreatedOn && !bCreatedOn) return 0
        if (!aCreatedOn) return 1
        if (!bCreatedOn) return -1
        return new Date(bCreatedOn).getTime() - new Date(aCreatedOn).getTime()
      }),
    [chatAppointmentEdges],
  )

  return (
    <ScrollWrapper className="p-2">
      <div className="grid gap-0.5" data-testid="chat-thread-list">
        {chatAppointmentEdgesNewestFirst.map((edge) => {
          const chatThread = getChatThreadByChatAppointment(edge.node)
          const isSelected = selectedThread?.id === chatThread.id

          return (
            <div
              onClick={() => handleClick(chatThread)}
              key={chatThread.id}
              className={cn(
                "hover:bg-surface-hovered grid h-auto min-h-15 grid-cols-1 grid-rows-[44px-16px] flex-row justify-center rounded-lg px-1 py-1 @md:grid-cols-[40px_1fr] @md:grid-rows-1 @md:gap-2 @md:px-2",
                isSelected && "bg-primary-surface!",
                isNavigationDisabled ? "cursor-not-allowed" : "cursor-pointer",
              )}>
              <div className="flex w-full content-center justify-center">
                <GenderIcon
                  className="hidden @md:m-auto @md:flex @md:h-10 @md:w-10"
                  size={30}
                  gender={chatThread.patient.gender}
                />
                <div className="relative content-end @md:hidden">
                  <UnreadMobileIndicator unreadCount={edge.node.chat?.readState?.unreadCount ?? 0} />
                  <div className="flex flex-row items-center gap-1">
                    <GenderIcon className="m-auto h-6 w-6 shrink-0" size={16} gender={chatThread.patient.gender} />
                    <p className="text-xs">{getShortName(chatThread.patient.firstName, chatThread.patient.lastName)}</p>
                  </div>
                </div>
              </div>
              <div className="flex justify-center">
                <ChatThreadItem chatThread={chatThread} appointmentRef={edge.node} selected={isSelected} />
              </div>
            </div>
          )
        })}
      </div>
    </ScrollWrapper>
  )
}
